<script setup>
import Button from "primevue/button";
import Avatar from "primevue/avatar";
import {AvatarGroup} from "primevue";
import {Link, router} from "@inertiajs/vue3";
import Dialog from "primevue/dialog";
import {getFormType, getInitials} from "../../../../helpers.js";
import DashboardCard from "../../components/ui-components/dashboard/DashboardCard.vue";
import {computed} from "vue";
import {useConfirm} from "primevue/useconfirm";
import ConfirmPopup from "primevue/confirmpopup";
import {trans} from "laravel-vue-i18n";

const confirm = useConfirm();

const props = defineProps({
    services: Array,
    service: Object,
    isExpanded: Boolean,
    selectedForm: Object,
    flags: [Object, Array],
    select: Array,
    canCreate: Boolean,
})

const groupedServices = computed(() => {
    const groups = {};
    for (const service of props.services) {
        if (props.selectedForm && service.reservation_forms?.id !== props.selectedForm.id) {
            continue;
        }

        const formId = service.reservation_forms?.id || 'no_form';
        if (!groups[formId]) {
            groups[formId] = {
                formName: service.reservation_forms?.name || null,
                color: service.reservation_forms?.theme?.color || 'gray',
                slug: service.reservation_forms?.slug,
                services: []
            };
        }
        groups[formId].services.push(service);
    }
    return groups;
});


const confirmDelete = (event, id) => {
    confirm.require({
        target: event.currentTarget,
        group: "services",
        message: trans('services.confirm_delete'),
        icon: 'pi pi-trash',
        rejectProps: {
            label: trans('common.cancel'),
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: trans('common.delete'),
            severity: 'danger'
        },
        accept: () => {
            router.delete(`/app/services/${id}`)
        },
        reject: () => {
            // Nepotřebuješ nic
        }
    });
};
</script>

<template>
    <ConfirmPopup group="services"></ConfirmPopup>

    <Dialog v-if="!!select" :visible="!!select" modal>
        <template #container>
            <div class="p-8 max-w-[600px] flex flex-col !relative gap-4">
                <Link href="/app/services" class="absolute right-6 top-6">
                    <Button class="w-8 h-8 !rounded-full" severity="secondary">
                    <i class="pi pi-times"></i>
                </Button>
                </Link>
                <i class="pi pi-table !text-3xl text-roori-blue"></i>
                <p class="text-xl font-bold">{{ $t('services.select_form_title') }}</p>
                <div class="grid grid-cols-3 dark:bg-neutral-800 bg-neutral-50 p-4 gap-4 rounded-xl max-h-[500px] overflow-y-auto">
                    <Link v-for="item in select" :key="item.id" :href="`/app/services/${item.slug}/create?back=${$page.url}`">
                        <div class="bg-white p-2 rounded border hover:shadow cursor-pointer h-full dark:bg-black dark:hover:bg-neutral-700 dark:border-neutral-700 hover:bg-neutral-50">
                            <div class="flex flex-col gap-1 justify-center items-center text-center overflow-hidden align-center">
                                                <div
                                                    class=" text-lg flex justify-center items-center"
                                                    :class="[`text-${item.theme?.color ?? 'blue'}-500 pi pi-briefcase`]">
                                                </div>
                                <div class="text-sm text-gray-900 dark:text-white">
                                    {{ item.name }}
                                </div>
                            </div>
                        </div>
                    </Link>
                </div>
            </div>
        </template>
    </Dialog>

    <div v-if="services.length" class="flex flex-col gap-8">
        <div v-for="(group, formId) in groupedServices" :key="formId" class="flex flex-col items-start last:border-b-0">
            <Link v-if="!selectedForm" :href="`/app/forms/${group.slug}`">
                <h3 v-if="group.formName"
                    class="mb-4 text-lg flex gap-2 hover:bg-neutral-200/80 cursor-pointer py-1 px-2 font-semibold rounded-lg dark:bg-gray-800 hover:dark:bg-gray-700 dark:text-white text-neutral-800 items-center">
                    <span class="pi pi-th-large" :class="`text-${group.color}-500`"></span>{{ group.formName }}<span class="border rounded-lg text-xs font-thin py-0.5 px-1">{{ $t('services.service_form_badge') }}</span></h3>
            </Link>
            <TransitionGroup
                tag="div"
                name="grid-move"
                class="grid gap-4 w-full"
                :class="isExpanded ? 'grid-cols-1  2xl:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 '"
            >
                <DashboardCard
                    v-for="_service in group.services"
                    :selected="_service.id === service?.id"
                    :key="_service.id"
                >
                    <template #image>
                        <div class="-mt-4 -mx-4  h-[100px] flex items-center justify-center mb-4 dark:bg-neutral-700 bg-neutral-100 overflow-hidden">
                            <img v-if="_service.image" :src="_service.image" alt="" class="w-full object-cover h-[100px] flex items-center justify-center"/>
                            <span v-else class="pi pi-camera !text-2xl opacity-50"></span>
                        </div>
                    </template>
                    <template #body>
                        <div class="mt-2 text-sm text-neutral-600 dark:text-neutral-300 space-y-3 grow">
                            <div class="flex flex-col !text-xs justify-between opacity-65">
                                <span>{{ $t('services.duration') }}: {{ _service.length_in_minutes }} min</span>
                                <span>{{ $t('services.price') }}: {{
                                        _service.min_price ? `${_service.min_price} Kč - ${_service.max_price} Kč` : `${_service.price} Kč`
                                    }}</span>
                            </div>
                        </div>
                    </template>
                    <template #header>
                        <div class="flex items-center justify-between gap-4 w-full">
                            <div class="flex items-center gap-3 overflow-hidden grow">
                                <h4 class="text-lg font-bold truncate text-gray-900 dark:text-white">
                                    {{ _service.name }}
                                </h4>
                            </div>
                        </div>
                    </template>
                    <template #main-actions>
                        <Link :href="`/app/services/${_service.id}${!!Object.values(flags).find(el => el === 'use_parent') ? `?form=${selectedForm.slug}&back=/app/forms/${selectedForm.slug}` : ''}`">
                            <Button
                                icon="pi pi-pencil"
                                rounded
                                text
                                severity="secondary"
                                v-tooltip.bottom="$t('services.tooltip_edit')"
                                class="hover:bg-gray-100 dark:hover:bg-gray-700"
                            />
                        </Link>
                        <Button
                            icon="pi pi-trash"
                            rounded
                            text
                            @click="confirmDelete($event, _service.id)"
                            severity="danger"
                            v-tooltip.bottom="$t('services.tooltip_delete')"
                            class="hover:bg-gray-100 dark:hover:bg-gray-700"
                        />
                    </template>
                    <template #secondary-actions>
                        <AvatarGroup v-if="_service.workers?.length">
                            <Avatar v-for="_worker in _service.workers" shape="circle"
                                    :label="getInitials(_worker.name)" v-tooltip.bottom="_worker.name"></Avatar>
                        </AvatarGroup>
                        <div v-else class="bg-neutral-100 p-1 border border-neutral-400 rounded-lg text-center opacity-50 text-xs">
                            {{ $t('services.no_workers') }}
                        </div>
                    </template>
                </DashboardCard>
            </TransitionGroup>
        </div>
    </div>
    <div v-else class="w-full p-16 h-full min-h-full flex flex-col gap-6 justify-center items-center text-center">
        <img src="../../../../../images/glass/Clock.png" alt="No clients illustration" class="w-48 h-48 mb-6 mx-auto" />
        <h2 class="text-2xl font-semibold text-gray-700 dark:text-gray-300">{{ $t('services.empty_title') }}</h2>
        <p class="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            <template v-if="!canCreate">
                {{ $t('services.empty_description') }}
            </template>
            <template v-else>
                {{ $t('services.empty_description_alt') }}
            </template>
        </p>

        <Link v-if="canCreate" href="/app/services/create">
            <Button
                :label="$t('services.btn_add')"
                icon="pi pi-plus"
                class="mt-4"
                severity="primary"
                rounded
            />
        </Link>
    </div>
</template>

<style scoped lang="scss">
.grid-move-move {
    transition: transform .25s ease, opacity .25s ease;
}

.grid-move-enter-active,
.grid-move-leave-active {
    transition: all .25s ease;
}

.grid-move-enter-from,
.grid-move-leave-to {
    opacity: 0;
    transform: scale(0.95);
}
</style>
